/**
 * **Die Konfetti-Formen aus Figma** (538-126860) — die Pfade stammen aus den
 * SVGs der Vorlage, je Grundform zwei bis vier Varianten. Figma zeigt dort ein
 * Einzelbild einer Lottie-Animation mit 48 Teilen; sie lassen sich auf vier
 * Formen zurückführen, und jede Form hat ihre eigenen Farben.
 *
 * Maße in den Einheiten des jeweiligen Pfads (= Pixel bei Größe 1).
 */

export type ConfettiKind = 'dot' | 'strip' | 'triangle' | 'streamer'

export interface ConfettiShape {
  kind: ConfettiKind
  /** SVG-Pfad, wie er aus Figma kommt. */
  d: string
  width: number
  height: number
  /** Nur Luftschlangen sind Striche statt Flächen. */
  strokeWidth?: number
}

export const CONFETTI_SHAPES: Record<ConfettiKind, readonly ConfettiShape[]> = {
  dot: [
    { kind: 'dot', width: 12.06, height: 10.79, d: 'M6.02858 0C9.35576 0 12.0572 2.41839 12.0572 5.397C12.0572 8.3756 9.35576 10.794 6.02858 10.794C2.70141 10.794 0 8.3756 0 5.397C0 2.41839 2.70141 0 6.02858 0Z' },
    { kind: 'dot', width: 6.73, height: 6.2, d: 'M3.3652 0C5.22245 0 6.73039 1.3883 6.73039 3.09819C6.73039 4.80808 5.22245 6.19637 3.3652 6.19637C1.50794 6.19637 0 4.80808 0 3.09819C0 1.3883 1.50794 0 3.3652 0Z' },
    { kind: 'dot', width: 3.7, height: 7.61, d: 'M1.85244 0C2.87479 0 3.70487 1.70588 3.70487 3.80691C3.70487 5.90795 2.87479 7.61383 1.85244 7.61383C0.830076 7.61383 0 5.90795 0 3.80691C0 1.70588 0.830076 0 1.85244 0Z' },
  ],
  strip: [
    { kind: 'strip', width: 4.4, height: 16.38, d: 'M2.83656 16.3818L0 11.575L1.55969 0L4.39625 4.80677L2.83656 16.3818Z' },
    { kind: 'strip', width: 9.2, height: 15.48, d: 'M5.93341 15.4778L0 10.9363L3.26249 0L9.19589 4.54152L5.93341 15.4778Z' },
    { kind: 'strip', width: 10.31, height: 18.07, d: 'M6.65142 18.0713L0 12.7688L3.65729 0L10.3087 5.30252L6.65142 18.0713Z' },
  ],
  triangle: [
    { kind: 'triangle', width: 16.35, height: 7.38, d: 'M8.17592 0L16.3518 7.3845H0L8.17592 0Z' },
    { kind: 'triangle', width: 14.48, height: 8.6, d: 'M7.23943 0L14.4789 8.60027L0 8.60027L7.23943 0Z' },
    { kind: 'triangle', width: 8.37, height: 11.36, d: 'M4.18262 0L8.36525 11.3595H0L4.18262 0Z' },
  ],
  streamer: [
    { kind: 'streamer', width: 31.7, height: 4.91, strokeWidth: 2, d: 'M31.6883 3.86245C20.647 3.95099 9.63427 4.24088 0.335129 0.945768' },
    { kind: 'streamer', width: 19.35, height: 4.68, strokeWidth: 3, d: 'M19.3045 3.13141C12.607 2.92022 6.03581 3.21087 0.459535 1.48328' },
    { kind: 'streamer', width: 18.63, height: 3.38, strokeWidth: 3, d: 'M18.5549 1.80578C12.5888 1.51517 6.1549 1.81291 0.0616196 1.57444' },
    { kind: 'streamer', width: 11.74, height: 4.34, strokeWidth: 2, d: 'M11.6655 3.3473C7.57892 3.02109 3.72891 2.3365 0.389212 0.913393' },
  ],
}

/** Die Farben aus Figma — jede Form nur in denen, die sie dort trägt. */
export const CONFETTI_COLORS: Record<ConfettiKind, readonly string[]> = {
  dot: ['#FF1A61', '#50E3C2'],
  strip: ['#FFE41D', '#50E3C2'],
  triangle: ['#2AB6FF', '#50E3C2'],
  streamer: ['#B91DFF', '#7ED321', '#FFE41D', '#FF1A61', '#50E3C2'],
}

/**
 * Wie oft jede Form vorkommt — nach der Verteilung in Figma (18 Punkte,
 * 11 Streifen, 12 Dreiecke, 8 Luftschlangen von 49).
 */
export const CONFETTI_MIX: Record<ConfettiKind, number> = {
  dot: 18,
  strip: 11,
  triangle: 12,
  streamer: 8,
}
